import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, ShieldCheck, Loader2, Clock } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useContractSignature, useStartSignature } from "@/lib/commercialQueries";

const READABLE_STATUS = {
  pending_signature: "Pending signature", changes_requested: "Changes requested", signed: "Signed",
  expired: "Expired", cancelled: "Cancelled", superseded: "Superseded", draft: "Draft",
};

/**
 * Master v5 Prompt 14 — the SignWell embedded signing surface. Tries an
 * iframe first (works when the audited production account allows
 * framing); the "Open in a new tab" link is ALWAYS shown alongside as
 * the secure redirect fallback, never hidden behind a failure state.
 *
 * Signed status is NEVER set here — this panel only ever POLLS
 * `/signature` (which itself only reads/reconciles from the provider)
 * and reflects whatever `contract_status` that reconciliation produced.
 * A redirect/iframe "success" screen from SignWell is not trusted;
 * only the next poll tick reflecting a provider-verified `signed`
 * status updates the UI.
 */
export function ContractSigningPanel({ workspaceId, contract, isClientActor, isViewAsClient }) {
  const [polling, setPolling] = useState(contract.status === "pending_signature" || contract.status === "changes_requested");
  const { data: sigData } = useContractSignature(workspaceId, contract.ctr_id, { enabled: polling });
  const startSignature = useStartSignature(workspaceId);

  const contractStatus = sigData?.contract_status || contract.status;
  const sgr = sigData?.signature_request;

  if (contractStatus === "signed") {
    return (
      <p className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium" data-testid="commercial-contract-signed-badge">
        <ShieldCheck size={15} /> Signed
      </p>
    );
  }
  if (["expired", "cancelled", "superseded", "draft"].includes(contractStatus)) {
    return <p className="text-sm text-ink-soft" data-testid="commercial-contract-status-readonly">{READABLE_STATUS[contractStatus]}</p>;
  }
  if (!isClientActor) {
    return <p className="text-sm text-ink-soft italic" data-testid="commercial-contract-awaiting-client">Waiting for the client to sign.</p>;
  }
  if (isViewAsClient) {
    return <p className="text-sm text-ink-soft italic" data-testid="commercial-contract-view-as-client-note">View as Client cannot sign this contract.</p>;
  }

  async function handleStart() {
    try {
      await startSignature.mutateAsync(contract.ctr_id);
      setPolling(true);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't start the signing flow");
    }
  }

  if (!sgr) {
    return (
      <div className="space-y-1.5">
        {contractStatus === "changes_requested" && (
          <p className="text-xs text-ink-soft italic" data-testid="commercial-changes-pending-note">
            You've requested changes — your coach will review them. You can still sign this version now if you're ready.
          </p>
        )}
        <Button onClick={handleStart} disabled={startSignature.isPending} data-testid="commercial-start-signing-btn">
          {startSignature.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
          Review &amp; sign contract
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="commercial-signing-panel">
      <p className="flex items-center gap-1.5 text-xs text-ink-soft" data-testid="commercial-signature-status">
        <Clock size={12} /> Signature status: <span className="font-medium capitalize">{sgr.status.replace("_", " ")}</span>
      </p>
      {contractStatus === "changes_requested" && (
        <p className="text-xs text-ink-soft italic" data-testid="commercial-changes-pending-note">
          You've requested changes — your coach will review them. Resuming below signs THIS version as-is.
        </p>
      )}
      {sgr.embedded_signing_url && (
        <>
          <a
            href={sgr.embedded_signing_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:underline"
            data-testid="commercial-signing-open-new-tab"
          >
            <ExternalLink size={13} /> Open the signing page in a new tab
          </a>
          <p className="text-[11px] text-ink-soft">Preview below — if it stays blank, use the link above (some accounts block embedding).</p>
          <div className="border border-ink/10 rounded-lg overflow-hidden bg-ink/[0.02]" style={{ height: 420 }}>
            <iframe
              src={sgr.embedded_signing_url}
              title="Sign your contract"
              className="w-full h-full"
              data-testid="commercial-signing-iframe"
            />
          </div>
        </>
      )}
    </div>
  );
}
